import { getRoleDisplayLabel } from './specialistRoleModuleHelper';
import { resolveEventTimestamp } from './flowHistoryDisplayHelper';

const pad2 = (value) => String(value).padStart(2, '0');

const toEventMs = (event) => {
  const raw = resolveEventTimestamp(event);
  const ms = new Date(raw || 0).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

export const formatBandejaDuration = (ms) => {
  const safeMs = Number.isFinite(Number(ms)) ? Number(ms) : 0;
  const totalSeconds = Math.max(0, Math.floor(safeMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${days} d, ${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
};

export const isBandejaBoundaryEvent = (event = {}) => {
  if (event?.ruleflow_id) {
    return true;
  }
  const reason = String(event?.reason_detail || '');
  return reason.startsWith('FLOW-IMPORT') || reason.startsWith('FLOW-CREATE');
};

const sortAsc = (events = []) =>
  [...(events || [])].sort((a, b) => {
    const dateA = toEventMs(a);
    const dateB = toEventMs(b);
    if (dateA !== dateB) {
      return dateA - dateB;
    }
    return Number(a.id || 0) - Number(b.id || 0);
  });

const findRole = (roleId, roles = []) =>
  (roles || []).find((item) => Number(item.id) === Number(roleId));

const isFeederRole = (roleId, roles = []) => {
  const name = String(findRole(roleId, roles)?.name || '').toLowerCase();
  return name.includes('feeder')
    || name.includes('alimentador')
    || name.includes('leader')
    || name.includes('lider')
    || name.includes('líder');
};

const resolveRoleLabel = (roleId, roles = [], t) => {
  const match = findRole(roleId, roles);
  if (!match?.name) {
    return roleId ? `#${roleId}` : '—';
  }
  return getRoleDisplayLabel(match.name, t);
};

const resolveStateName = (stateId, states = []) =>
  (states || []).find((item) => Number(item.id) === Number(stateId))?.name || '';

const isPublishedState = (name = '') => {
  const normalized = String(name).trim().toLowerCase();
  return normalized === 'publicado' || normalized === 'published';
};

const isRejectedState = (name = '') => {
  const normalized = String(name).trim().toLowerCase();
  return normalized === 'rechazado' || normalized === 'rejected';
};

const isReturnAction = (event = {}) => {
  const value = String(event.rule?.value || event.rule?.description || '').toLowerCase();
  return value.includes('return') || value.includes('devolv');
};

const resolveNodeLabel = ({ event, roleLabel, stateName, t, isReturn }) => {
  if (isPublishedState(stateName)) {
    return t('general.flow-engine.semaphore.published', 'Publicado');
  }
  if (isRejectedState(stateName)) {
    return t('general.flow-engine.semaphore.rejected', 'Rechazado');
  }
  if (isReturn) {
    return t('general.flow-engine.history.diagram.return-to', 'Devolución {{role}}', { role: roleLabel });
  }
  return roleLabel;
};

export const buildFlowBandejaAnalytics = ({
  events = [],
  roles = [],
  states = [],
  t = (key, fallback) => fallback || key,
  now = new Date()
} = {}) => {
  const boundaries = sortAsc((events || []).filter((event) => isBandejaBoundaryEvent(event)));
  const stays = [];
  const nodes = [];
  let totalMs = 0;
  const seenRoles = new Set();

  boundaries.forEach((event, index) => {
    const next = boundaries[index + 1];
    const start = toEventMs(event);
    const end = next ? toEventMs(next) : now.getTime();
    const durationMs = Math.max(0, end - start);
    const roleId = event.catalogrole_id ? Number(event.catalogrole_id) : null;
    const stateName = resolveStateName(event.state_catalog_id, states);
    const terminal = isPublishedState(stateName) || isRejectedState(stateName);
    const roleLabel = roleId
      ? resolveRoleLabel(roleId, roles, t)
      : (terminal ? (isPublishedState(stateName)
        ? t('general.flow-engine.semaphore.published', 'Publicado')
        : t('general.flow-engine.semaphore.rejected', 'Rechazado')) : '—');
    const isReturn = Boolean(roleId && seenRoles.has(roleId) && isReturnAction(event));

    if (roleId && !terminal) {
      const lastStay = stays[stays.length - 1];
      const continueStay = lastStay
        && Number(lastStay.roleId) === Number(roleId)
        && !isReturn;

      if (continueStay) {
        lastStay.totalMs += durationMs;
      } else {
        stays.push({
          id: event.id,
          roleId,
          roleLabel,
          isReturn,
          totalMs: durationMs
        });
      }
      totalMs += durationMs;
      seenRoles.add(roleId);
    }

    const sameBandejaAsPrevious = nodes.length
      && Number(nodes[nodes.length - 1].roleId) === Number(roleId)
      && !terminal
      && !isReturn;

    if (!sameBandejaAsPrevious) {
      nodes.push({
        id: event.id,
        roleId,
        roleLabel,
        label: resolveNodeLabel({ event, roleLabel, stateName, t, isReturn }),
        action: event.rule?.description || event.rule?.value || '',
        motivo: isReturn ? (event.reason_detail || '') : '',
        isReturn,
        isTerminal: terminal,
        casePhase: event.case_phase || '',
        phaseLabel: isFeederRole(roleId, roles)
          ? t('flow.case-phase.termino-clave', 'Término Clave')
          : '',
        at: resolveEventTimestamp(event)
      });
    }
  });

  const rows = stays.map((row) => ({
    ...row,
    formatted: formatBandejaDuration(row.totalMs)
  }));

  return {
    rows,
    totalMs,
    formattedTotal: formatBandejaDuration(totalMs),
    nodes,
    isClosed: nodes.some((node) => node.isTerminal)
  };
};

